import React, { useState, useRef, useEffect, useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import AppsIcon from '@mui/icons-material/Apps';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/** Palette Alliance Digital — App switcher */
const ALLIANCE = {
  primary: '#E85C0D',
  secondary: '#333333',
  neutral: '#666666',
  light: '#FFFFFF',
  sidebarBg: '#2C2C2C',
  success: '#198754',
  danger: '#dc3545',
};

const APPLICATIONS = [
  {
    name: 'GCA',
    url: 'https://gca.alliance-digital.fr',
    Icon: AppsIcon,
  },
  {
    name: 'Support',
    url: 'https://support.alliance-digital.fr',
    Icon: AppsIcon,
  },
  {
    name: 'Gestions',
    url: 'https://gestion.alliance-digital.fr',
    Icon: AppsIcon,
  },
];

function AppSwitcher() {
  const colors = useMemo(
    () => ({
      ...ALLIANCE,
      border: alpha(ALLIANCE.secondary, 0.18),
      primaryLight: alpha(ALLIANCE.primary, 0.14),
      rowHover: alpha(ALLIANCE.primary, 0.08),
    }),
    [],
  );
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Détecter l'application actuelle basée sur l'URL
  const currentApp = useMemo(() => {
    const currentUrl = window.location.origin;
    return APPLICATIONS.find(app => app.url === currentUrl) || APPLICATIONS[2]; // Gestions par défaut
  }, []);

  // Fermer le menu si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const buttonStyle = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 12px',
      backgroundColor: colors.sidebarBg,
      border: `1px solid ${alpha(ALLIANCE.light, 0.12)}`,
      borderRadius: '6px',
      cursor: 'pointer',
      color: colors.light,
      fontSize: '14px',
      fontWeight: 500,
      transition: 'all 0.2s ease',
      position: 'relative',
    }),
    [colors],
  );

  const dropdownStyle = useMemo(
    () => ({
      position: 'absolute',
      top: '100%',
      left: 0,
      marginTop: '4px',
      backgroundColor: colors.light,
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      boxShadow: `0 8px 24px ${alpha(ALLIANCE.secondary, 0.18)}`,
      minWidth: '200px',
      zIndex: 1000,
      overflow: 'hidden',
    }),
    [colors],
  );

  const getAppItemStyle = (app, isLast, isCurrent) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    cursor: 'pointer',
    color: colors.secondary,
    fontSize: '14px',
    transition: 'background-color 0.2s ease',
    borderBottom: isLast ? 'none' : `1px solid ${colors.border}`,
    borderLeft: isCurrent ? `3px solid ${colors.success}` : '3px solid transparent',
    backgroundColor: isCurrent ? colors.primaryLight : 'transparent',
  });

  const handleAppClick = (app, event) => {
    // Si c'est l'application actuelle, juste fermer le menu
    if (app.url === window.location.origin) {
      setIsOpen(false);
      return;
    }

    // Détecter Ctrl+clic ou Cmd+clic (Mac) pour ouvrir dans un nouvel onglet
    if (event.ctrlKey || event.metaKey) {
      window.open(app.url, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = app.url;
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          ...buttonStyle,
          backgroundColor: isOpen ? alpha(colors.sidebarBg, 0.92) : colors.sidebarBg,
        }}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = alpha(colors.sidebarBg, 0.88);
            e.currentTarget.style.borderColor = alpha(ALLIANCE.primary, 0.45);
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = colors.sidebarBg;
            e.currentTarget.style.borderColor = alpha(ALLIANCE.light, 0.12);
          }
        }}
      >
        <currentApp.Icon sx={{ fontSize: 18, color: colors.primary }} />
        <span>{currentApp.name}</span>
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: alpha(colors.light, 0.85),
            transition: 'transform 0.2s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      {isOpen && (
        <div style={dropdownStyle}>
          {APPLICATIONS.map((app, index) => {
            const isLast = index === APPLICATIONS.length - 1;
            const isCurrent = app.name === currentApp.name;
            return (
              <div
                key={index}
                onClick={(e) => handleAppClick(app, e)}
                style={getAppItemStyle(app, isLast, isCurrent)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isCurrent
                    ? colors.primaryLight
                    : colors.rowHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isCurrent
                    ? colors.primaryLight
                    : 'transparent';
                }}
              >
                <app.Icon sx={{ fontSize: 18, color: colors.primary }} />
                <span style={{ flex: 1 }}>{app.name}</span>
                {!isCurrent && (
                  <OpenInNewIcon sx={{ fontSize: 16, color: colors.neutral }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AppSwitcher;

